/** React Query hooks for the Audio Studio (keys under `['audio-studio', …]`). */
import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  audioStudioApi,
  type AttachmentsQuery,
  type LibraryTracksParams,
  type ListTracksParams,
  type StudioJob,
} from '@/api/audioStudioApi';
import { useAuth } from '@/contexts/AuthContext';
import { useStudioPageState } from '@/cowork/launch/useStudioViewer';

export const audioKeys = {
  all: ['audio-studio'] as const,
  presets: ['audio-studio', 'presets'] as const,
  tracks: (p: ListTracksParams) => ['audio-studio', 'tracks', p] as const,
  tracksAll: ['audio-studio', 'tracks'] as const,
  job: (id: string) => ['audio-studio', 'job', id] as const,
  members: ['audio-studio', 'team-members'] as const,
  attachments: (q: AttachmentsQuery) => ['audio-studio', 'attachments', q] as const,
  attachmentsAll: ['audio-studio', 'attachments'] as const,
  trends: (region: string, period: number) => ['audio-studio', 'trends', region, period] as const,
  trendVideos: (trendId: string, region: string) => ['audio-studio', 'trend-videos', trendId, region] as const,
  /** Prefix of every collections key — invalidate it after any collection change (list + details). */
  collectionsAll: ['audio-studio', 'collections'] as const,
  collections: (brandId: string | null = null) => ['audio-studio', 'collections', 'list', brandId] as const,
  collection: (id: string) => ['audio-studio', 'collections', 'detail', id] as const,
  library: (p: LibraryTracksParams) => ['audio-studio', 'library', 'tracks', p] as const,
  libraryFilters: ['audio-studio', 'library', 'filters'] as const,
  playlists: ['audio-studio', 'library', 'playlists'] as const,
  playlist: (id: string) => ['audio-studio', 'library', 'playlist', id] as const,
};

export const JOB_POLL_MS = 3000;

export function isJobActive(job: Pick<StudioJob, 'status'> | undefined): boolean {
  return !job || job.status === 'queued' || job.status === 'running';
}

/**
 * Who may use the Audio Studio (page, Ad Studio card, sidebar, search and the Soundtracks blocks on beats /
 * collaborations), since the client launch (Nov 2026): Beatly staff per Cowork `GET /v1/me`, and a client team only
 * when Beatly switched `audio` on for it (and has not paused clients). `null` while /v1/me loads, so nothing flashes
 * for a client; signed out = no. The rule is the launch shell's (cowork/clientFeatures.ts studioPageState, through
 * cowork/launch/useStudioViewer.ts), so the page, the shell and every Audio entry point agree; when /v1/me never
 * answered only a show_cowork account with a positive staff sign gets in. The audio service checks the same /v1/me.
 */
export function useAudioStudioAccess(): boolean | null {
  const { user } = useAuth();
  const state = useStudioPageState('audio-studio');
  if (!user) return false;
  if (state === 'loading') return null;
  return state === 'open';
}

/** {@link useAudioStudioAccess} as a plain yes/no (unknown = no). */
export function useAudioStudioEnabled(): boolean {
  return useAudioStudioAccess() === true;
}

export function usePresets() {
  return useQuery({ queryKey: audioKeys.presets, queryFn: () => audioStudioApi.presets(), staleTime: 30 * 60_000, retry: 1 });
}

export function useStudioTracks(params: ListTracksParams, enabled = true) {
  return useQuery({ queryKey: audioKeys.tracks(params), queryFn: () => audioStudioApi.tracks(params), enabled });
}

/** Polls a job every 3 s while it is queued/running; stops on succeeded/failed. Shared by `useJob` and "Recent creations". */
export function jobQueryOptions(jobId: string) {
  return queryOptions({
    queryKey: audioKeys.job(jobId),
    queryFn: () => audioStudioApi.job(jobId),
    enabled: Boolean(jobId),
    refetchInterval: (q) => (isJobActive(q.state.data) ? JOB_POLL_MS : false),
    refetchIntervalInBackground: true,
    retry: 2,
    staleTime: 0,
  });
}

export function useJob(jobId: string | null | undefined) {
  return useQuery(jobQueryOptions(jobId || ''));
}

export function useTeamMembers(enabled: boolean) {
  return useQuery({ queryKey: audioKeys.members, queryFn: () => audioStudioApi.teamMembers(), enabled, staleTime: 10 * 60_000 });
}

export function useAttachments(q: AttachmentsQuery, enabled = true) {
  return useQuery({ queryKey: audioKeys.attachments(q), queryFn: () => audioStudioApi.attachments(q), enabled, retry: false });
}

/** How often, and how many times, to re-ask while TikTok song previews are being refreshed (≈ 1 minute). */
const PREVIEW_REFRESH_MS = 5_000;
const PREVIEW_REFRESH_TRIES = 12;

/**
 * TikTok trends. TikTok's song preview links expire after a few hours, so a stale cached list comes back without
 * previews while the service fetches a fresh one in the background. Re-ask every few seconds until the previews
 * are back (bounded), so "Play song" turns on by itself instead of waiting for a reload.
 */
export function useAudioTrends(region: string, period: number) {
  return useQuery({
    queryKey: audioKeys.trends(region, period),
    queryFn: () => audioStudioApi.trends({ region, period }),
    staleTime: 10 * 60_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      const refreshing = data?.source === 'tiktok_cml' && (data.items?.length ?? 0) > 0
        && data.items.every((t) => !t.preview_url);
      return refreshing && query.state.dataUpdateCount <= PREVIEW_REFRESH_TRIES ? PREVIEW_REFRESH_MS : false;
    },
  });
}

/** Real TikTok songs (`cml-…` ids) have top videos; fixture / sample songs never do. */
export function trendHasVideoSource(trendId: string): boolean {
  return trendId.startsWith('cml-');
}

/**
 * A song's top TikTok videos (GET /trends/audio/{id}/videos). Shared by the trend cards (loaded once a card
 * scrolls into view) and the song drawer, so opening the drawer reuses what the card already fetched.
 */
export function useTrendVideos(trendId: string, region: string, enabled = true) {
  return useQuery({
    queryKey: audioKeys.trendVideos(trendId, region),
    queryFn: () => audioStudioApi.trendVideos(trendId, region),
    enabled: enabled && trendHasVideoSource(trendId),
    staleTime: 30 * 60_000,
  });
}

/** The team's collections (most recently updated first); `brandId` keeps only that brand's. */
export function useCollections(enabled = true, brandId: string | null = null) {
  return useQuery({
    queryKey: audioKeys.collections(brandId),
    queryFn: () => audioStudioApi.collections(brandId ? { brand_id: brandId } : {}),
    enabled,
  });
}

export function collectionQueryOptions(id: string) {
  return queryOptions({ queryKey: audioKeys.collection(id), queryFn: () => audioStudioApi.collection(id), enabled: Boolean(id), retry: false });
}

/** One collection with its sounds. */
export function useCollection(id: string | null) {
  return useQuery(collectionQueryOptions(id || ''));
}

export function useLibraryTracks(params: LibraryTracksParams, enabled = true) {
  return useQuery({ queryKey: audioKeys.library(params), queryFn: () => audioStudioApi.library.tracks(params), enabled, placeholderData: (prev) => prev });
}

export function useLibraryFilterOptions() {
  return useQuery({ queryKey: audioKeys.libraryFilters, queryFn: () => audioStudioApi.library.filterOptions(), staleTime: 30 * 60_000 });
}

export function usePlaylists() {
  return useQuery({ queryKey: audioKeys.playlists, queryFn: () => audioStudioApi.library.playlists(), staleTime: 10 * 60_000 });
}

export function usePlaylist(id: string | null) {
  return useQuery({ queryKey: audioKeys.playlist(id || ''), queryFn: () => audioStudioApi.library.playlist(id!), enabled: Boolean(id) });
}
